/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef } from 'react';
import { Button, Spinner, Typography } from '@tempmee/react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { colors } from '@tempmee/tailwind';
import { Center, Column, Divider, Row } from '~/commonComponents';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { useProfessionOptions } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { firePermPostJobWorkDetailsSubmitted } from '~/posthogs/events';
import { NumberUtils } from '~/utils/NumberUtils';
import { pluralize } from '~/utils/String';
import {
  AutoSaveStatus,
  EarningBenefitForm,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import { ScheduleForm } from '../JobDetailsForm/components/ScheduleInfo';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';

export const WorkDetailsPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);
  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const scheduleRef = useRef<HTMLDivElement>(null);
  const earningsAndBenefitsRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.Schedule]: scheduleRef,
      [CreatePermanentJobPostingFormSection.EarningsAndBenefits]: earningsAndBenefitsRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.JobDescription,
    triggers: isEnforcePostAJobFunnelImprovement
      ? [
          'benefits',
          'benefitsOther',
          'compensationType',
          'expectedDays',
          'minRate',
          'payType',
          'scheduleType',
        ]
      : [
          'benefits',
          'benefitsOther',
          'compensationType',
          'expectedDays',
          'maxRate',
          'minRate',
          'payType',
          'scheduleType',
        ],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobWorkDetailsSubmitted({
        postingId,
        professionType: formData.professionType,
      });
    },
  });
  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  const user = useUserSelector();
  const {
    countsByProfession,
    isLoading: isCountLoading,
    isError: isCountError,
  } = useGetProfessionalCountsByOffice(user?.id, {
    enable: isEnforcePostAJobFunnelImprovement,
    distance: 25,
  });

  const { findByCode } = useProfessionOptions();
  const professionTypeValue = controller.watch('professionType');
  const professionTypeLabel = findByCode(professionTypeValue)?.label ?? '';

  const totalCount = countsByProfession
    ? (countsByProfession[professionTypeValue as keyof typeof countsByProfession] ?? 0)
    : 0;

  const showCount =
    isEnforcePostAJobFunnelImprovement && !isCountLoading && !isCountError && totalCount > 0;

  if (isLoading) {
    return (
      <Center marginBlock={48}>
        <Spinner centered variant="default" size="md" />
      </Center>
    );
  }

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
        <Column alignItems="center" gap={16}>
          <Column alignItems="center" gap={10}>
            <Typography
              as="span"
              bold
              size="heading_2"
              color={colors.neutral[600]}
              textAlign="center"
            >
              {showCount
                ? // eslint-disable-next-line max-len
                  `${NumberUtils.applyThousandSeparator(totalCount)} ${pluralize(professionTypeLabel, totalCount)} in Your Area`
                : 'Dental Professionals Near You Are on GoTu'}
            </Typography>
            <Typography as="span" size="body_large" color={colors.neutral[500]} textAlign="center">
              Just set your schedule and compensation and we'll get your job in front of them.
            </Typography>
          </Column>
          {hasFormErrors && <GeneralErrorMessage />}
          <FormProvider {...controller}>
            <Column width="100%" maxWidth={800} alignSelf="center">
              <InfoSection
                ref={scheduleRef}
                title="Schedule & Pay"
                titleFontSize={24}
                titleColor="neutral_600"
              >
                <Column gap={24}>
                  <ScheduleForm control={controller.control} errors={controller.formState.errors} />
                  <Divider height={1} marginVertical={0} />
                  <EarningBenefitForm />
                </Column>
              </InfoSection>
            </Column>
          </FormProvider>
          <Row justifyContent="center" marginTop={24}>
            <Button onClick={onSave} isLoading={isPending || isAutoSaveLoading} size="lg">
              Continue
            </Button>
          </Row>
        </Column>
      </>
    );
  }

  return (
    <>
      <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
      <Column width="60%" alignSelf="center">
        <Typography as="span" bold size="heading_4" style={{ marginBottom: 14, marginLeft: 24 }}>
          Step 2: Work Details
        </Typography>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <Column gap={16}>
            <InfoSection ref={scheduleRef} icon="calendar-solid" title="Schedule">
              <ScheduleForm control={controller.control} errors={controller.formState.errors} />
            </InfoSection>
            <InfoSection
              ref={earningsAndBenefitsRef}
              icon="money-pay-solid"
              title="Earnings and Benefits"
            >
              <EarningBenefitForm />
            </InfoSection>
          </Column>
        </FormProvider>
        <Row justifyContent="center" marginTop={24}>
          <Button onClick={onSave} isLoading={isPending || isAutoSaveLoading} size="lg">
            Next
          </Button>
        </Row>
      </Column>
    </>
  );
};
